import { useState } from "react";
import { Activity, TrendingUp, Users, ShoppingCart } from "lucide-react";
import { cn } from "@cieda/djayma-ui";
import AdminLayout from "@/components/admin/AdminLayout";
import { AdminDataTable } from "@/components/admin/shared/AdminDataTable";
import type { ColumnDef } from "@/components/admin/shared/AdminDataTable";
import { KPICard } from "@/components/admin/shared/KPICard";
import { useAgentPerformance } from "@/hooks/admin/useOperations";
import type { AgentPerformanceMetrics } from "@/lib/admin-api/operations";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PERIODS = [
  { value: "current_month", label: "Ce mois" },
  { value: "previous_month", label: "Mois précédent" },
  { value: "quarter", label: "Trimestre" },
] as const;

type Period = (typeof PERIODS)[number]["value"];

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatFcfa(amount: number): string {
  return `${amount.toLocaleString("fr-SN")} FCFA`;
}

function formatPercent(value: number): string {
  return `${new Intl.NumberFormat("fr-SN", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value)} %`;
}

// ---------------------------------------------------------------------------
// Objective progress cell
// ---------------------------------------------------------------------------

interface ObjectiveProgressProps {
  readonly value: number;
}

function ObjectiveProgress({ value }: ObjectiveProgressProps) {
  const color =
    value >= 100
      ? "text-emerald-700"
      : value >= 80
        ? "text-amber-700"
        : "text-red-700";

  const barColor =
    value >= 100
      ? "bg-emerald-500"
      : value >= 80
        ? "bg-amber-500"
        : "bg-red-500";

  return (
    <div className="flex min-w-[100px] flex-col gap-1">
      <span className={`text-xs font-semibold ${color}`}>{formatPercent(value)}</span>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-stone-100">
        <div
          className={cn("h-full rounded-full transition-all", barColor)}
          style={{ width: `${Math.min(100, value)}%` }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function AgentPerformance() {
  const [period, setPeriod] = useState<Period>("current_month");

  const { data, isLoading } = useAgentPerformance(period);

  const agents = data?.agents ?? [];

  // Aggregate KPIs
  const totalVisits = agents.reduce((sum, a) => sum + a.visits_this_month, 0);
  const totalOrders = agents.reduce((sum, a) => sum + a.orders_taken, 0);
  const totalRevenue = agents.reduce((sum, a) => sum + a.revenue_generated_fcfa, 0);
  const avgAchievement =
    agents.length > 0
      ? agents.reduce((sum, a) => sum + a.target_achievement_percent, 0) / agents.length
      : 0;

  const columns: readonly ColumnDef<AgentPerformanceMetrics>[] = [
    {
      id: "rank",
      header: "#",
      cell: (row) => (
        <span className="font-semibold text-stone-500">{row.rank}</span>
      ),
    },
    {
      id: "agent_name",
      header: "Agent",
      cell: (row) => (
        <span className="font-medium text-stone-900">{row.agent_name}</span>
      ),
    },
    {
      id: "region",
      header: "Région",
      accessorKey: "region",
    },
    {
      id: "visits_this_month",
      header: "Visites",
      cell: (row) => (
        <span className="text-stone-700">
          {row.visits_this_month.toLocaleString("fr-SN")}
        </span>
      ),
    },
    {
      id: "orders_taken",
      header: "Commandes",
      cell: (row) => (
        <span className="text-stone-700">
          {row.orders_taken.toLocaleString("fr-SN")}
        </span>
      ),
    },
    {
      id: "revenue_generated_fcfa",
      header: "CA (FCFA)",
      cell: (row) => (
        <span className="font-medium text-stone-900">
          {formatFcfa(row.revenue_generated_fcfa)}
        </span>
      ),
    },
    {
      id: "avg_order_value_fcfa",
      header: "Panier moyen",
      cell: (row) => (
        <span className="text-stone-600">
          {formatFcfa(row.avg_order_value_fcfa)}
        </span>
      ),
    },
    {
      id: "on_time_checkins_percent",
      header: "Check-ins à temps",
      cell: (row) => (
        <span className="text-stone-600">
          {formatPercent(row.on_time_checkins_percent)}
        </span>
      ),
    },
    {
      id: "target_achievement_percent",
      header: "Objectif",
      cell: (row) => (
        <ObjectiveProgress value={row.target_achievement_percent} />
      ),
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Page header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
              <Activity className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-semibold text-stone-900">
                Performance agents
              </h1>
              <p className="text-sm text-stone-500">
                {data?.period ?? "Période en cours"}
              </p>
            </div>
          </div>

          {/* Period selector */}
          <div className="flex items-center gap-1 rounded-lg border border-stone-200 bg-white p-1">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                className={cn(
                  "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                  period === p.value
                    ? "bg-amber-600 text-white"
                    : "text-stone-600 hover:bg-stone-50",
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <KPICard
            label="Total visites"
            value={totalVisits}
            icon={<Users className="h-4 w-4" />}
          />
          <KPICard
            label="Commandes totales"
            value={totalOrders}
            icon={<ShoppingCart className="h-4 w-4" />}
          />
          <KPICard
            label="CA généré"
            value={totalRevenue}
            format="currency"
            icon={<TrendingUp className="h-4 w-4" />}
          />
          <KPICard
            label="Atteinte objectif moy."
            value={avgAchievement}
            format="percent"
          />
        </div>

        {/* Ranking table */}
        <AdminDataTable
          columns={columns}
          data={[...agents].sort((a, b) => a.rank - b.rank)}
          isLoading={isLoading}
          emptyMessage="Aucune donnée de performance disponible."
        />
      </div>
    </AdminLayout>
  );
}
